import { useEffect, useRef, useState } from 'react';

export interface PickedInlineCellProps {
  qtyPicked: number;
  qtyRequired: number;
  /** Modificabile: dipende dallo stato della lista, deciso dal chiamante. */
  editable: boolean;
  /** Evidenzia come inevaso (rosso). Ignorato quando la riga è modificabile in corso. */
  inevaso: boolean;
  onSave: (qty: number) => Promise<void>;
  ariaLabel: string;
}

/**
 * La quantità prelevata, **modificabile direttamente nel campo**.
 *
 * Si salva uscendo dal campo o con Invio; si annulla con Esc, e il valore torna
 * com'era. Nessun pulsante accanto.
 *
 * ⚠️ **È un ritorno al comportamento anteriore al 29 luglio**, quando l'input in
 * linea fu sostituito con una finestra di conferma. Non è una svista da
 * correggere: il cliente ha chiesto di tornare indietro, perché con la
 * correzione diventata un gesto frequente una finestra per ogni riga è troppo.
 *
 * Il valore fuori intervallo non parte: si segnala e basta. Chiamare il server
 * per sentirsi dire ciò che si sapeva già è solo un modo più lento di dire no.
 *
 * Finché il valore resta sotto il richiesto il campo ha lo **sfondo ambra**
 * (`vp-picked-incomplete`): in una tabella da centinaia di righe è l'unico modo
 * di vedere a colpo d'occhio quali tengono ferma la lista.
 */
export function PickedInlineCell({
  qtyPicked, qtyRequired, editable, inevaso, onSave, ariaLabel,
}: PickedInlineCellProps) {
  const [valore, setValore] = useState(String(qtyPicked));
  const [errore, setErrore] = useState(false);
  const [inCorso, setInCorso] = useState(false);
  const ultimoSalvato = useRef(qtyPicked);

  // Il prelevato cambia anche da fuori: l'operatore preleva, il controllo
  // periodico aggiorna il dettaglio. Senza questo, il campo resterebbe fermo
  // sul valore vecchio mentre la riga sotto dice altro.
  useEffect(() => {
    if (!inCorso) {
      setValore(String(qtyPicked));
      ultimoSalvato.current = qtyPicked;
    }
  }, [qtyPicked, inCorso]);

  if (!editable) {
    return (
      <span className={inevaso ? 'text-danger fw-semibold' : undefined}>
        {qtyPicked} / {qtyRequired}
      </span>
    );
  }

  const annulla = () => {
    setValore(String(ultimoSalvato.current));
    setErrore(false);
  };

  // "Non ancora al richiesto", letto su quel che si sta SCRIVENDO e non
  // sull'ultimo valore salvato: chi porta il campo a valore pieno deve vedere
  // l'evidenza sparire subito, altrimenti non sa se ha finito.
  //
  // Un valore non valido non e' incompleto: e' sbagliato, e lo dice gia' il
  // bordo rosso. Due segnali sovrapposti sullo stesso campo si annullano.
  const n = Number(valore);
  const incompleto = valore.trim() !== '' && Number.isInteger(n)
    && n >= 0 && n < qtyRequired;

  const salva = async () => {
    if (valore.trim() === '' || !Number.isInteger(n) || n < 0 || n > qtyRequired) {
      setErrore(true);
      return;
    }
    if (n === ultimoSalvato.current) {
      setErrore(false);
      return; // niente da salvare: non si disturba il server per nulla
    }
    setErrore(false);
    setInCorso(true);
    try {
      await onSave(n);
      ultimoSalvato.current = n;
    } catch {
      annulla();
    } finally {
      setInCorso(false);
    }
  };

  return (
    <span className="d-inline-flex align-items-center gap-1 justify-content-end">
      <input
        type="number"
        min={0}
        max={qtyRequired}
        step={1}
        aria-label={ariaLabel}
        aria-invalid={errore || undefined}
        disabled={inCorso}
        className={'form-control form-control-sm text-end'
                   + (errore ? ' is-invalid' : '')
                   + (incompleto && !errore ? ' vp-picked-incomplete' : '')
                   + (inevaso && !errore ? ' text-danger fw-semibold' : '')}
        style={{ width: 72 }}
        value={valore}
        onChange={(e) => { setValore(e.target.value); setErrore(false); }}
        onBlur={() => void salva()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); void salva(); }
          if (e.key === 'Escape') { e.preventDefault(); annulla(); }
        }}
      />
      <span className="text-muted small">/ {qtyRequired}</span>
    </span>
  );
}
